/* eslint-disable @typescript-eslint/naming-convention */

import { type JsonMap, segment } from "../client"
import type { AppScreen, ExternalScreenType } from "@/constants/Screens"

export type ComponentType =
  | "google_auth_button"
  | "apple_auth_button"
  | "google_install_badge"
  | "apple_install_badge"
  | "button"
  | "banner"
  | "check_box"
  | "text_link"
  | "list_option"
  | "icon"
  | "input_field"
  | "menu_option"
  | "pill"
  | "toast"
  | "toggle"
  | "text"
  | "widget-analyze"
  | "widget-loans"
  | "widget-rewards"
  | "widget-preapproval"
  | "widget-properties"
  | "widget-marketing"
  | "widget-appdownload"
  | "tab_menu_option"
  | "map_place_icon"
  | "question_answer"
  | "walkthrough"

type ListType = "property" | "comp" | "loan product list"
type CardType =
  | "property"
  | "comp list"
  | "comp photo"
  | "comp carousel photo"
  | "capital product"

interface ScreenViewedEvent {
  current_screen: AppScreen
  from_screen?: AppScreen
  current_tab?: string | null
  screen_state?: string | null
  screen_variant?: string
  property_address?: string | null
  viewed_values?: JsonMap
  marketing_widget_title?: string | null
}

export const createScreenViewedEvent = ({
  current_screen,
  from_screen = "Unknown",
  current_tab,
  screen_state,
  screen_variant,
  property_address,
  viewed_values = {},
  marketing_widget_title,
}: ScreenViewedEvent): [string, JsonMap] => {
  return [
    `${current_screen} Viewed`,
    {
      current_screen,
      from_screen,
      current_tab,
      screen_state,
      screen_variant,
      property_address,
      viewed_values,
      marketing_widget_title,
    },
  ]
}

interface SubmittedEvent {
  object_name: string
  current_screen: AppScreen
  to_screen?: AppScreen
  component_type: ComponentType
  component_text: string
  submitted_values?: JsonMap
}

export const createSubmittedEvent = ({
  object_name,
  current_screen,
  to_screen = "Unknown",
  component_type,
  component_text,
  submitted_values = {},
}: SubmittedEvent): [string, JsonMap] => {
  return [
    `${object_name} Submitted`,
    {
      current_screen,
      to_screen,
      component_type,
      component_text,
      submitted_values,
    },
  ]
}

interface ComponentClickedEvent {
  object_name?: string
  current_screen: AppScreen
  to_screen?: AppScreen | undefined
  component_id?: string
  component_type: ComponentType
  component_text?: string
  component_function: string
  component_variant?: string
  action_taken?: string
  clicked_values?: JsonMap
}

export const createComponentClickedEvent = ({
  object_name,
  current_screen,
  to_screen,
  component_id,
  component_type,
  component_text,
  component_function,
  component_variant,
  action_taken,
  clicked_values = {},
}: ComponentClickedEvent): [string, JsonMap] => {
  return [
    `Component Clicked${object_name ? ` - ${object_name}` : ""}`,
    {
      current_screen,
      to_screen,
      component_id,
      component_type,
      component_text,
      component_function,
      component_variant,
      action_taken,
      clicked_values,
    },
  ]
}

interface ComponentViewedEvent {
  current_screen: AppScreen
  component_type: ComponentType
  component_text?: string
  component_function: string
  component_size?: string
  component_position?: string
  component_variant?: string
}

export const createComponentViewedEvent = ({
  current_screen,
  component_type,
  component_text,
  component_function,
  component_size,
  component_position,
  component_variant,
}: ComponentViewedEvent): [string, JsonMap] => {
  return [
    `Component Viewed`,
    {
      current_screen,
      component_type,
      component_text,
      component_function,
      component_size,
      component_position,
      component_variant,
    },
  ]
}

interface ListScrolledEvent {
  current_screen: AppScreen
  list_type: ListType
  scrolled_to: number
  interaction_type?: string
}

export const createListScrolledEvent = ({
  current_screen,
  list_type,
  scrolled_to,
}: ListScrolledEvent): [string, JsonMap] => {
  return [
    `List Scrolled`,
    {
      current_screen,
      list_type,
      scrolled_to,
    },
  ]
}

export const createAccordionOpenEvent = ({
  current_screen,
  accordion_key,
  user = false,
}: {
  current_screen: AppScreen
  accordion_key: string
  user?: boolean
}): [string, JsonMap] => {
  return [
    `Accordion Opened`,
    {
      current_screen,
      accordion_key,
      user,
    },
  ]
}

interface CardClickedEvent {
  current_screen: AppScreen
  to_screen: AppScreen
  card_type: CardType
  card_list_number: number
  card_values?: JsonMap
}

export const createCardClickedEvent = ({
  current_screen,
  to_screen,
  card_type,
  card_list_number,
  card_values,
}: CardClickedEvent): [string, JsonMap] => {
  return [
    `Card Clicked`,
    {
      current_screen,
      to_screen,
      card_type,
      card_list_number,
      card_values,
    },
  ]
}

interface ExternalLinkClickedEvent {
  current_screen: AppScreen
  to_screen: ExternalScreenType
  component_type: ComponentType
  link_text: string
  link_url: string
}

export const createExternalLinkClickedEvent = ({
  current_screen,
  to_screen,
  component_type,
  link_text,
  link_url,
}: ExternalLinkClickedEvent): [string, JsonMap] => {
  return [
    `Component Clicked`,
    {
      current_screen,
      to_screen,
      component_type,
      link_text,
      link_url,
    },
  ]
}

interface AdEvent {
  event_name: string
  utm_medium: string | null
  utm_source: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
}

export const createAdEvent = ({
  event_name,
  utm_medium,
  utm_source,
  utm_campaign,
  utm_content,
  utm_term,
}: AdEvent): [string, JsonMap] => {
  return [
    event_name,
    {
      utm_medium,
      utm_source,
      utm_campaign,
      utm_content,
      utm_term,
    },
  ]
}

export const createVariantEvent = (
  flag: string,
  value: string | boolean
): [string, JsonMap] => {
  return [
    `Variant Used`,
    {
      variant_flag: flag,
      variant_value: value,
    },
  ]
}

interface GenericActionEvent {
  event_name: string
  current_screen: AppScreen
  component_type: ComponentType
  values: JsonMap
}

export const createGenericActionEvent = ({
  event_name,
  current_screen,
  component_type,
  values,
}: GenericActionEvent): [string, JsonMap] => {
  return [
    event_name,
    {
      current_screen,
      component_type,
      values,
    },
  ]
}

interface ErrorEvent {
  event_name: string
  message: string
}

export const createErrorEvent = ({
  event_name,
  message,
}: ErrorEvent): [string, JsonMap] => {
  return [
    event_name,
    {
      message,
    },
  ]
}

export const track = (event: [string, JsonMap]) => {
  if (typeof segment?.track === "undefined") {
    return
  }

  void segment.track(...event)
}
